import type { ReactElement } from "react"
import { useContext } from "react"

import { useTranslation } from "react-i18next"

import type { Serie } from "@nivo/line"
import { Typography } from "@synapse-analytics/synapse-ui"

import { ChartsContainer } from "../../../../../components/graphs/ChartsContainer"
import { BaseLineChart } from "../../../../../components/graphs/Line/BaseLineChart"
import { Context } from "../../../Context"

import styles from "./LineChart.module.scss"

type LineChartProps = {
  lineChartData: Serie[]
  title: string
  isLoading: boolean
  graphHeight: number
  tooltipText: string
}

/**
 * A component to display a line chart with the non-performing loans and approval rates.
 *
 * The chart displays the non-performing loans on the x-axis and approval rates on the y-axis.
 * The user can hover over the chart to see the exact values of the data points.
 *
 * @param {LineChartProps} props - The props for the LineChart component.
 * @param {Serie[]} lineChartData - The data to be displayed in the chart.
 * @param {string} title - The title of the chart.
 * @param {number} graphHeight - The height of the chart.
 * @param {boolean} isLoading - Whether the chart is in a loading state.
 * @param {string} tooltipText - The text to be displayed in the tooltip.
 * @returns {ReactElement} The rendered LineChart component.
 */
export function LineChart(props: Readonly<LineChartProps>): ReactElement {
  const { lineChartData, title, graphHeight, isLoading, tooltipText } = props

  const { currentThresholdMetricData } = useContext(Context)
  const { t } = useTranslation("ModelConfigurations")

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight}
      tooltipText={tooltipText}
      gutterBottom
    >
      <BaseLineChart
        data={lineChartData}
        chartOptions={{
          curve: "monotoneX",
          margin: { top: 16, bottom: 50, left: 40, right: 20 },
          yScale: { type: "linear", min: 0, max: 100 },
          xScale: { type: "linear", min: 0, max: 100 },
          axisBottom: {
            legend: t("charts.lineChart.approvalRate"),
            legendOffset: 36,
            legendPosition: "end",
          },
          axisTop: {
            format: () => "",
            legend: t("charts.lineChart.nonPerformingLoans"),
            legendOffset: -10,
            legendPosition: "start",
          },
          tooltip: ({ point }) => {
            return (
              <div className={`${styles.tooltip} chart-tooltip`}>
                <div>
                  <Typography variant="label-bold">{t("charts.lineChart.nplAbbr")}</Typography>{" "}
                  <Typography variant="label-bold">{point.data.yFormatted}</Typography>
                </div>
                <div>
                  <Typography variant="label-bold">{t("charts.lineChart.approvalRate")}</Typography>{" "}
                  <Typography variant="label-bold">{point.data.xFormatted}</Typography>
                </div>
              </div>
            )
          },
          markers: [
            {
              axis: "x",
              value: +(currentThresholdMetricData?.AR ?? 0),
              legend: t("charts.lineChart.approvalRate"),
              legendOrientation: "vertical",
              legendPosition: "right",

              lineStyle: {
                strokeWidth: 0.5,
                strokeDasharray: "10",
                stroke: "var(--base-text-1)",
              },
              textStyle: { fontSize: "12px", fontWeight: "400", fill: "var(--base-text-3)" },
            },
            {
              axis: "y",
              value: +(currentThresholdMetricData?.NPL ?? 0),
              legend: t("charts.lineChart.nonPerformingLoans"),
              legendOrientation: "horizontal",
              legendPosition: "top",

              lineStyle: {
                strokeWidth: 0.5,
                strokeDasharray: "10",
                stroke: "var(--base-text-1)",
              },
              textStyle: { fontSize: "12px", fontWeight: "400", fill: "var(--base-text-3)" },
            },
          ],
          layers: ["axes", "crosshair", "grid", "legends", "mesh", "points", "lines", "areas", "slices", "markers"],
        }}
      />
    </ChartsContainer>
  )
}
